import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Home, ShoppingBag, Users, Wallet, Link as LinkIcon, Scale, UserCircle, Building2, Headset } from 'lucide-react';
import { useAuth } from '@mondivago/supabase';
import { UnifiedSidebar } from '@/components/sidebars/UnifiedSidebar';
import { Tooltip } from '@/components/ui';
import { usePartnerMordModal, usePartnerAccount, usePartner, usePartnerProfile } from '@/hooks/partners';
import { PartnerMordModal, SupportTicketModal } from '@/components/modals';
import { getSupabaseImageUrl } from '@/shared/lib/image';

interface PartnerSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

const PARTNER_MENU_ITEMS = [
  { path: '/partner', label: 'Dashboard', icon: Home, end: true },
  { path: '/partner/bookings', label: 'Bookings', icon: ShoppingBag },
  { path: '/partner/links', label: 'Links', icon: LinkIcon },
  { path: '/partner/wallet', label: 'Wallet', icon: Wallet },
  { path: '/partner/team', label: 'Team', icon: Users },
  { path: '/partner/partner-settings', label: 'Partner Settings', icon: Building2 },
  { path: '/partner/account-settings', label: 'Account Settings', icon: UserCircle },
];

export function PartnerSidebar({ isCollapsed, setIsCollapsed }: PartnerSidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { worker } = usePartnerAccount();
  const { resolved } = usePartner(user?.id);
  const { profile } = usePartnerProfile(user?.id);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const {
    showModal: showLegalModal,
    activeAgreement,
    hasNewVersion,
    loading: legalLoading,
    handleClose: closeLegalModal,
    openModal: openLegalModal,
    refresh: refreshLegalModal,
    acceptAgreement,
  } = usePartnerMordModal();

  const isOwner =
    !!profile || (!!worker && (worker.role === 'owner' || worker.role === 'admin' || worker.role === 'agency'));
  const isTeamWorker = !isOwner && !!worker && (worker.role === 'operator' || worker.role === 'worker');

  const hiddenPaths = useMemo(() => {
    const list: string[] = [];
    if (isTeamWorker) {
      list.push('/partner/team', '/partner/wallet', '/partner/partner-settings');
    }
    if (profile?.type === 'affiliate') {
      // Affiliates get Dashboard, Links, Wallet, Partner Settings and Account Settings.
      list.push('/partner/bookings', '/partner/team');
    } else if (profile?.type) {
      // Resellers hide the affiliate-only Links page.
      list.push('/partner/links');
    }
    return list;
  }, [isTeamWorker, profile?.type]);

  useEffect(() => {
    if (hiddenPaths.some((path) => location.pathname.startsWith(path))) {
      void navigate('/partner', { replace: true });
    }
  }, [hiddenPaths, location.pathname, navigate]);

  const menuItems = useMemo(() => PARTNER_MENU_ITEMS.filter((item) => !hiddenPaths.includes(item.path)), [hiddenPaths]);

  const userPhoto =
    worker?.photo_url ??
    (user?.user_metadata?.avatar_url as string | undefined) ??
    (user?.user_metadata?.picture as string | undefined) ??
    null;

  const profileInitials = useMemo(() => {
    const name =
      (worker ? `${worker.first_name ?? ''} ${worker.last_name ?? ''}`.trim() : '') ||
      profile?.company_name ||
      resolved?.partner_name ||
      (user?.user_metadata?.full_name as string | undefined) ||
      (user?.user_metadata?.name as string | undefined) ||
      user?.email ||
      '';
    const parts = name.trim().split(' ');
    if (parts.length < 2) {
      return name ? name.slice(0, 2).toUpperCase() : 'PA';
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [worker, profile?.company_name, resolved?.partner_name, user?.user_metadata, user?.email]);

  const breadcrumbs = useMemo(() => {
    const segments: { label: string; to: string }[] = [{ label: 'Partner', to: '/partner' }];

    if (location.pathname === '/partner/account-settings') {
      segments.push({ label: 'Account Settings', to: '/partner/account-settings' });
    } else if (location.pathname === '/partner/partner-settings') {
      segments.push({ label: 'Partner Settings', to: '/partner/partner-settings' });
    } else if (location.pathname === '/partner/bookings') {
      segments.push({ label: 'Bookings', to: '/partner/bookings' });
    } else if (location.pathname === '/partner/links') {
      segments.push({ label: 'Links', to: '/partner/links' });
    } else if (location.pathname === '/partner/wallet') {
      segments.push({ label: 'Wallet', to: '/partner/wallet' });
    } else if (location.pathname === '/partner/team') {
      segments.push({ label: 'Team', to: '/partner/team' });
    }

    return segments;
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    void navigate('/', { replace: true });
  };

  const legalButton = (
    <Tooltip content="Legal Agreement" position="right" className="w-full" disabled={!isCollapsed}>
      <button
        type="button"
        onClick={openLegalModal}
        className={`group relative flex items-center rounded-xl py-3 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
      >
        <Scale className="h-5 w-5 shrink-0" />
        {!isCollapsed && <span>Legal Agreement</span>}
      </button>
    </Tooltip>
  );

  const supportButton = (
    <Tooltip content="Support" position="right" className="w-full" disabled={!isCollapsed}>
      <button
        type="button"
        onClick={() => setSupportModalOpen(true)}
        className={`group relative flex items-center rounded-xl py-3 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
      >
        <Headset className="h-5 w-5 shrink-0" />
        {!isCollapsed && <span>Support</span>}
      </button>
    </Tooltip>
  );

  const headerActions = (
    <div className="flex items-center gap-3">
      <Link
        to="/partner/account-settings"
        className="flex items-center transition-transform hover:scale-105 active:scale-95"
        title="Account Settings"
      >
        <div className="relative overflow-visible md:h-9 md:w-9 h-10 w-10">
          <div className="h-full w-full overflow-hidden rounded-full border border-gray-100 ring-2 ring-lime-400 md:ring-0 ring-offset-2 ring-offset-white bg-gray-50 shadow-sm">
            {userPhoto ? (
              <img
                src={getSupabaseImageUrl(userPhoto, 'public', { width: 96, height: 96, format: 'webp' })}
                alt="Avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-lime-100 text-xs font-black text-lime-900">
                {profileInitials}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );

  return (
    <>
      <UnifiedSidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        brandLink="/partner"
        menuItems={menuItems}
        breadcrumbs={breadcrumbs}
        headerActions={headerActions}
        onSignOut={handleSignOut}
        showHelp={false}
        extraFooterItems={
          <>
            {supportButton}
            {legalButton}
          </>
        }
      />

      <PartnerMordModal
        isOpen={showLegalModal}
        onClose={closeLegalModal}
        userId={user?.id}
        activeAgreement={activeAgreement}
        loading={legalLoading}
        alreadySigned={!legalLoading && activeAgreement !== null && !hasNewVersion}
        onAccept={acceptAgreement}
        onAcknowledged={() => {
          refreshLegalModal();
          closeLegalModal();
        }}
      />

      <SupportTicketModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        contactEmail={user?.email ?? ''}
      />
    </>
  );
}
